/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo, useState } from 'react';

import { CollectionsModule } from '@coursera-web/cds-labs';

import type { Skill } from '@coursera/grpc-types-skills/coursera/proto/skills/v2/skill';

import BrowseInSearchCollectionsPlaceholder from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSearchCollectionsPlaceholder';
import {
  getBrowseInSearchCollectionsData,
  mapSearchHitsToCollections,
} from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSerpUtils';
import {
  BROWSE_IN_SEARCH_BEGINNER_INDEX_ID,
  BROWSE_IN_SEARCH_MOST_POPULAR_INDEX_ID,
} from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData } from 'bundles/search-common/types';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { isSearchProductHit } from 'bundles/search-common/utils/utils';
import { useSeoPageData } from 'bundles/seo/contexts/SeoPageDataContext';

const styles = {
  container: css`
    margin-bottom: var(--cds-spacing-400);
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
  `,
  customChip: css`
    &.cds-Chip-root {
      border: 1px solid var(--cds-color-neutral-stroke-primary);
      background-color: var(--cds-color-white-0);

      &:hover,
      &:focus {
        background-color: var(--cds-color-grey-25);
        border: 1px solid var(--cds-color-neutral-disabled-strong);
        color: var(--cds-color-neutral-primary);
      }
    }

    &.cds-Chip-selected {
      background-color: var(--cds-color-grey-975);
      border: 1px solid var(--cds-color-grey-975);
      color: var(--cds-color-white-0);

      &:hover,
      &:focus {
        background-color: var(--cds-color-grey-975);
        border: 1px solid var(--cds-color-grey-975);
        color: var(--cds-color-white-0);
      }
    }
  `,
};
type Props = { eventingData?: SearchPageEventingData; skill: Skill };

const BrowseInSearchCollections = ({ skill, eventingData }: Props) => {
  const isEqp = useIsEqp();
  const { eqpData } = useSeoPageData();
  const topContentBlock = eqpData?.topContentBlock || undefined;
  const { getSearchResults, isLoading } = useSearch();
  const mostPopularData = getSearchResults(BROWSE_IN_SEARCH_MOST_POPULAR_INDEX_ID);
  const beginnerFriendlyData = getSearchResults(BROWSE_IN_SEARCH_BEGINNER_INDEX_ID);
  const mostPopularHits = useMemo(() => mostPopularData?.elements.filter(isSearchProductHit), [mostPopularData]);
  const beginnerFriendlyHits = useMemo(
    () => beginnerFriendlyData?.elements.filter(isSearchProductHit),
    [beginnerFriendlyData]
  );
  const { headline, description, collectionSets, layout, isCollectionsLoading, filters } =
    getBrowseInSearchCollectionsData(skill, mostPopularHits, beginnerFriendlyHits, isLoading) || {};

  const [currentCollectionSetIndex, setCurrentCollectionSetIndex] = useState(0);
  const handleFilterChange = (index: number) => {
    setCurrentCollectionSetIndex(index);
  };

  if (isCollectionsLoading) {
    return <BrowseInSearchCollectionsPlaceholder />;
  }
  if (!collectionSets || (collectionSets?.length === 0 && !isCollectionsLoading)) return null;

  const searchCollections = mapSearchHitsToCollections(collectionSets);

  return (
    <div css={styles.container} data-testid="browse-in-search-collections">
      <CollectionsModule
        eventingData={eventingData}
        productCardTrackingName="search_card"
        filters={filters && filters.length > 0 ? filters : null}
        onFilterChange={handleFilterChange}
        currentCollectionSetIndex={currentCollectionSetIndex}
        collections={[searchCollections[currentCollectionSetIndex]]}
        numOfEntriesPerCollection={4}
        showAllEntries={true}
        isCollectionsLoading={isCollectionsLoading}
        headline={isEqp ? topContentBlock?.label : headline}
        description={isEqp ? topContentBlock?.content : description}
        layout={layout}
        backgroundColor="grey"
        showRoundedCorners
        customChipStyles={styles.customChip}
        alwaysShowChipFilters={true}
      />
    </div>
  );
};

export default BrowseInSearchCollections;
